var kind                 = require("enyo/kind"),
    Control              = require("enyo/Control"),
    UiComponent          = require("enyo/UiComponent"),
    CanvasControl        = require("enyo-canvas/CanvasControl"),
    CanvasRenderDelegate = require("enyo-canvas/CanvasRenderDelegate"),
    CanvasRenderer       = require("enyo-canvas/CanvasRenderer");

/**
 * An object describing the bounds of the viewport in canvas space.
 *
 * @typedef   {Object}  enyo-canvas.Viewport
 * @property  {number}  x       The x coordinate of the top-left corner of the
 *                              viewport in canvas space.
 * @property  {number}  y       The y coordinate of the top-left edge of the
 *                              viewport in canvas space.
 * @property  {number}  width   The width of the viewport in canvas pixels
 * @property  {number}  height  The height of the viewport in canvas pixels
 */

/**
 * [Canvas]{@link enyo-canvas.Canvas} is a specialized Control for use as an
 * HTMLCanvasElement tag.
 *
 * It implements the infrastructure needed to set up a
 * [CanvasRenderer]{@link enyo-canvas.CanvasRenderer} to render nested
 * components and setup a viewport to map the canvas tag to canvas space.
 *
 * This control also has a modified reflow method which allows the renderer to
 * scale canvas space in response to changes in dimensions of the canvas tag and
 * provides a hook to resize the viewport if needed.
 *
 * @class   enyo-canvas.Canvas
 * @extends enyo.Control
 * @public
 */
module.exports = kind(/** @lends enyo-canvas.Canvas.prototype */ {

    /**
     * @private
     */
    name: "enyo-canvas.Canvas",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    defaultKind: CanvasControl,

    /**
     * @private
     */
    tag: "canvas",

    /**
     * @private
     */
    renderDelegate: CanvasRenderDelegate,

    /**
     * The type of renderer used for this canvas
     *
     * @type {enyo-canvas.CanvasRenderer}
     * @public
     */
    rendererKind: CanvasRenderer,

    /**
     * Optionally leave some extra options for the renderer.
     *
     * @type {?Object}
     * @public
     */
    rendererOptions: {},

    /**
     * Whether the scene needs to be (partially) re-drawn.
     *
     * @type {boolean}
     * @public
     */
    dirty: true,

    /**
     * A cached return value of `getBoundingClientRect()` on the canvas element.
     *
     * @type {?DOMRect}
     * @readonly
     * @public
     */
    boundingClientRect: null,

    /**
     * The viewport bounds
     *
     * @type {?enyo-canvas.Viewport}
     * @public
     */
    viewport: null,

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createRenderer();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    createRenderer: function() {
        var Renderer = this.rendererKind,
            options  = this.rendererOptions || {};

        this.renderer = new Renderer(options);
        this.renderer.set("scene", this);
    },

    // Destruction
    // --------------------------------

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            var renderer = this.renderer;

            renderer.destroy && renderer.destroy();
            sup.apply(this, arguments);
        };
    }),

    // Canvas Entities
    // --------------------------------

    /**
     * The `addChild()` method of `enyo.Control` assumes that the child being
     * added is an instance of `enyo.Control`. Because CanvasControls are not
     * instances of `enyo.Control`, we instead call the `addChild()` method on
     * `enyo.UiComponent`, the super kind of `enyo.Control`.
     *
     * @param {Object}  child   The child
     * @param {Object}  before  The child before which to insert our new child
     * @private
     */
    addChild: function(child, before) {
        child.set("scene", this);
        UiComponent.prototype.addChild.call(this, child, before);
    },

    /**
     * The `removeChild()` method of `enyo.Control` assumes that the child being
     * removed is an instance of `enyo.Control`. Because CanvasControls are not
     * instances of `enyo.Control`, we instead call the `removeChild()` method
     * on `enyo.UiComponent`, the super kind of `enyo.Control`.
     *
     * @param {Object}  child  The child
     * @private
     */
    removeChild: function(child) {
        UiComponent.prototype.removeChild.call(this, child);
        child.set("scene", null);
    },

    // DOM Rendering
    // --------------------------------

    /**
     * Canvas tags do not have any content.
     *
     * @return {string}  Empty string
     * @private
     */
    generateInnerHtml: function() {
        return "";
    },

    /**
     * Canvas tags have no content to tear down.
     *
     * @private
     */
    teardownChildren: function() {},

    /**
     * Now that we have a canvas tag, pass it to the renderer.
     *
     * @private
     */
    rendered: function() {
        if (this.__isLazyControl) {
            this.refreshAbsoluteShowing();
        }
        this.renderer.setupScene();
        this.reflow();
        this.renderer.start();
    },

    /**
     * Also destroy any setupScene to render contexts and off-screen canvas.
     *
     * @method
     * @private
     */
    teardownRender: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.renderer.teardownScene();
            this.boundingClientRect = null;
            this.viewport           = null;
            this.set("dirty", true);
        };
    }),

    // Scene Update
    // --------------------------------

    /**
     * Allow all children to update their state.
     *
     * @protected
     */
    update: function() {
        var children        = this.children,
            expiredChildren = [],
            i,
            n;

        // Search for expired childrene
        for (i = 0, n = children.length; i < n; i++) {
            if (children[i].get("expired")) {
                expiredChildren.push(children[i]);
            }
        }

        // Destroy expired children
        expiredChildren.forEach(function(child) {
            child.destroy();
        });

        // Update the remaining children
        for (i = 0, n = children.length; i < n; i++) {
            children[i].updateState();
        }
    },

    // Resize Scene
    // --------------------------------

    /**
     * Stop propagation of DOM resize event to children.
     *
     * @return {boolean}  Stops propagation
     * @private
     */
    handleResize: function() {
        if (this.hasNode()) {
            this.reflow();
        }

        return true;
    },

    /**
     * @private
     */
    reflow: function() {
        var canvas = this.hasNode(),
            newBounds;

        // No canvas
        if (!canvas) {
            return;
        }

        // In order to allow css to scale the canvas tag, we need to remove it's
        // sizing attributes.
        canvas.removeAttribute("width");
        canvas.removeAttribute("height");

        // Determine the size of the canvas node and scale the scene
        // accordingly.
        newBounds = canvas.getBoundingClientRect();
        this.renderer.scaleScene(newBounds);

        // Some browsers do some pixel rounding so we need to re-check the
        // bounds before cacheing them.
        this.boundingClientRect = canvas.getBoundingClientRect();
        this.setupViewport();
    },

    /**
     * Setup initial viewport. By default the the viewport bounds are the same
     * as the canvas bounds with no transform offset. Override this method as
     * needed.
     *
     * @protected
     */
    setupViewport: function() {
        var bounds = this.boundingClientRect,
            viewport;

        if (!bounds) {
            return;
        }

        viewport   = {
            width:  bounds.width,
            height: bounds.height
        };
        viewport.x = 0;
        viewport.y = 0;
        this.renderer.setTransform(1, 0, 0, 1, viewport.x + viewport.width, viewport.y + viewport.height);
        this.set("viewport", viewport);
    }

});
